import {
  type FC,
  forwardRef,
  type JSX,
  type MutableRefObject,
  type RefObject,
  useImperativeHandle,
  useRef,
} from "react";
import type { AriaButtonProps, AriaTooltipProps, OverlayTriggerProps } from "react-aria";
import { type OverlayTriggerState } from "react-stately";
import {
  type OverlayOptions,
  type PopoverProps,
  Tooltip,
  type TooltipOptions,
  type TooltipProps,
  useDialogWithPosition,
  useTooltip,
} from "widgets";

interface IPopoverControllerProps {
  isOffscreen?: boolean;
  withForm?: boolean; // use this flag to avoid exposing the dialog ref as "ref", because redux-form does not cope with ForwardRef
  actionWidget: FC<IActionWidgetPropsInjectedFromController>;
  children: FC<IPopoverPropsInjectedFromController>;
  overlayOptions?: OverlayOptions & OverlayTriggerProps;
}

export interface IPopoverPropsInjectedFromController extends Pick<PopoverProps, "closeFn" | "domProps" | "arrowProps"> {
  dialogRef: MutableRefObject<any>;
  ref: MutableRefObject<any>;
}

export interface IActionWidgetPropsInjectedFromController {
  onPress: OverlayTriggerState["open"];
  ref: MutableRefObject<any>;
  domProps: AriaButtonProps;
}

/** @deprecated */
export function PopoverController(props: IPopoverControllerProps): JSX.Element {
  const { dialogProps, state, triggerProps, arrowProps, triggerRef, dialogRef } = useDialogWithPosition(
    props.overlayOptions || {},
  );

  const actionWidget = props.actionWidget?.({
    onPress: state.open,
    ref: !props.isOffscreen ? triggerRef : undefined,
    domProps: triggerProps,
  });
  const children = state.isOpen
    ? props.children?.({
        closeFn: state.close,
        dialogRef: !props.isOffscreen ? dialogRef : undefined,
        ref: !props.isOffscreen && !props.withForm ? dialogRef : undefined,
        domProps: dialogProps,
        arrowProps,
      })
    : null;

  return (
    <>
      {actionWidget}
      {children}
    </>
  );
}

/**
 * Easy way to use a Tooltip component for the simplest and standard cases.
 */
interface ITooltipControllerProps extends Omit<TooltipProps, "domProps" | "arrowProps" | "children"> {
  children: FC<any>; // tooltip target
  overlayOptions?: TooltipOptions & OverlayOptions;
  targetRef?: RefObject<Element | null>; // if the target ref is controlled by the parent component
}

/** @deprecated */
export const TooltipController = forwardRef<any, ITooltipControllerProps>(
  function TooltipController(props, ref): JSX.Element {
    const { children, overlayOptions, ...tooltipControllerProps } = props;

    const triggerRef = useRef<Element>(null);
    const tooltipRef = useRef<HTMLDivElement>(null);
    const { triggerProps, tooltipProps, arrowProps, state, updatePosition } = useTooltip(
      props.targetRef ?? triggerRef,
      tooltipRef,
      {
        placement: "top",
        delay: 300,
        offset: 5,
        ...overlayOptions,
        isDisabled: overlayOptions?.isDisabled || !tooltipControllerProps?.content,
        noPointerClickEvents: true,
      },
    );

    // allows imperative call for updating the tooltip position
    useImperativeHandle(ref, () => ({
      updateTooltipPosition: updatePosition,
    }));

    const target = children?.({
      ...triggerProps,
      ref: props.targetRef ?? triggerRef,
    } as AriaTooltipProps);

    return (
      <>
        {target}
        {state.isOpen && (
          <Tooltip domProps={tooltipProps} arrowProps={arrowProps} ref={tooltipRef} {...tooltipControllerProps} />
        )}
      </>
    );
  },
);

/**
 * The popover can be controlled (open/close) by the parent, for example if there are multiple ways to open it.
 */

interface IControlledPopoverProps {
  triggerRef: MutableRefObject<any>;
  close: () => void;
  children: FC<IPopoverPropsInjectedFromController>;
}

export function ControlledPopover(props: IControlledPopoverProps): JSX.Element {
  const { dialogProps, arrowProps, dialogRef } = useDialogWithPosition<HTMLDivElement, HTMLDivElement>(
    {
      isOpen: true,
      onClose: props.close,
    },
    props.triggerRef,
  );

  const children = props.children?.({
    closeFn: props.close,
    ref: dialogRef,
    dialogRef: dialogRef,
    domProps: dialogProps,
    arrowProps,
  });

  return <>{children}</>;
}
